<script setup lang="ts">
import { useUI } from '~/composables/ui/useUI'

const route = useRoute()

const { navigation } = useUI()
const { desktopNavItems, mobileTabs, moreItems } = navigation

const isMoreOpen = ref(false)
function toggleMore() {
  isMoreOpen.value = !isMoreOpen.value
}

const showMore = computed(() => moreItems.value.length > 0)
const isLandingRoute = computed(() => route.path === '/')
// The privacy policy and terms sit on the landing page's paper, edge to edge like it.
const isLegalRoute = computed(() => route.path === '/privacy' || route.path === '/terms')
// Claims render on the same paper, so moving from Home to Claims keeps one visual world.
const isClaimsRoute = computed(() => route.path === '/claims' || route.path.startsWith('/claims/'))
// Apply and Membership too: joining, and coming back as a member, stay on one paper.
const isApplyRoute = computed(() => route.path === '/apply' || route.path.startsWith('/apply/'))
const isMembershipRoute = computed(() => route.path === '/membership' || route.path.startsWith('/membership/'))
// Balances sits beside Membership in every member's nav, so it keeps the same paper.
const isBalancesRoute = computed(() => route.path === '/balances' || route.path.startsWith('/balances/'))
// The vendor portal is about claims, so it reads like the member's claims pages.
const isVendorsRoute = computed(() => route.path === '/vendors' || route.path.startsWith('/vendors/'))
// And the pool admin, the last pages that were still on the old look.
const isPoolRoute = computed(() => route.path === '/pool' || route.path.startsWith('/pool/'))
const useLandingSurface = computed(() =>
  isLandingRoute.value || isLegalRoute.value || isClaimsRoute.value || isApplyRoute.value || isMembershipRoute.value
  || isBalancesRoute.value || isVendorsRoute.value || isPoolRoute.value,
)

const isMoreActive = computed(() => showMore.value && moreItems.value.some(item => isActivePath(route.path, item.path)))
</script>

<template>
  <div>
    <a
      href="#main"
      class="sr-only focus:not-sr-only focus:absolute focus:m-2 focus:rounded-sm focus:bg-ink focus:px-3 focus:py-2 focus:text-paper"
    >
      Skip to content
    </a>

    <!--
      One layout, one copy of the page. Only the chrome differs by breakpoint: each header
      is hidden by CSS on the other side of md, and the bottom tab bar is mobile-only.
      Rendering the whole layout twice doubled the HTML, the hydration work and every
      element id on the page.
    -->
    <AppLayoutTemplate
      has-bottom-nav
      :surface="useLandingSurface ? 'transparent' : 'default'"
      :fluid="useLandingSurface"
    >
      <template #header>
        <AppNavHeader
          class="max-md:hidden"
          variant="desktop"
          :nav-items="desktopNavItems"
        >
          <template #actions>
            <WalletAuth />
            <UIThemeToggle />
          </template>
        </AppNavHeader>

        <AppNavHeader class="md:hidden" variant="mobile">
          <template #actions>
            <UIThemeToggle />
            <WalletAuth size="sm" />
          </template>
        </AppNavHeader>
      </template>

      <slot />

      <template #page-footer>
        <AppLegalFooter />
      </template>

      <template #footer>
        <div class="md:hidden">
          <AppMoreMenu
            v-model="isMoreOpen"
            :items="moreItems"
            :active-path="route.path"
          />

          <AppBottomTabBar
            :tabs="mobileTabs"
            :active-path="route.path"
            :show-more="showMore"
            :is-more-open="isMoreOpen"
            :is-more-active="isMoreActive"
            @toggle-more="toggleMore"
          />
        </div>
      </template>
    </AppLayoutTemplate>
  </div>
</template>
